import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

// Server stores all dates/times in UTC — convert to the viewer's local timezone for display.
export function formatLocalTime(utcDate, utcTime) {
  if (!utcDate || !utcTime) return utcTime || '';
  try {
    const [year, month, day] = utcDate.split('-').map(Number);
    const [hours, minutes] = utcTime.split(':').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day, hours, minutes));
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch {
    return utcTime;
  }
}

/**
 * A score summary as TEXT, whatever shape it arrives in.
 *
 * `score_summary` is a JSON column. For a multi-game match it holds
 * `{ matches: [{ participant_a_score, participant_b_score }, ...] }`, and
 * rendering that object straight into JSX throws "Objects are not valid as a
 * React child (found: object with keys {matches})" — which trips the error
 * boundary and takes the ENTIRE app down, bottom navigation included. Any live
 * game that had been scored did it.
 *
 * So: pass strings through, turn a matches array into the scoreline a person
 * expects ("11-8, 9-11"), and render nothing for anything else rather than
 * risking the app on an unexpected shape.
 */
export function scoreSummaryText(summary) {
  if (summary == null) return '';
  if (typeof summary === 'string' || typeof summary === 'number') return String(summary);
  const matches = Array.isArray(summary) ? summary : summary.matches;
  if (!Array.isArray(matches)) return '';
  return matches
    .map((m) => {
      const a = m?.participant_a_score ?? m?.a;
      const b = m?.participant_b_score ?? m?.b;
      return a == null || b == null ? null : `${a}-${b}`;
    })
    .filter(Boolean)
    .join(', ');
}

// Shared link-resolution logic for a live event (used by Header + mobile Live panel).
export function getLiveEventLink(event) {
  if (event.game?.liveLink) return event.game.liveLink;
  const eventId = event.id;
  const gameId = event.game?.id;
  if (event.ownerType === 'group') {
    let url = `/squads/${event.ownerId}?tab=current-event&eventId=${eventId}`;
    if (gameId) url += `&gameId=${gameId}`;
    return url + '&openGame=true';
  }
  if (event.ownerType === 'tournament') return `/tournament/${event.ownerId}`;
  return `/events/${eventId}`;
}

const STATUS_CONFIG = {
  in_progress:      { label: 'In Progress',       cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  active:           { label: 'Active',            cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  setup:            { label: 'Setting Up',        cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' },
  player_selection: { label: 'Selecting Players', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' },
};

/** "Live Now" event list — shared by the desktop Header dropdown and the mobile Live panel. */
const LiveEventsList = ({ liveEvents, onEventClick }) => {
  const { theme } = useTheme();

  return (
    <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
      {liveEvents.map((event) => {
        const game = event.game;
        const timeRange = [
          formatLocalTime(event.date, event.startTime),
          formatLocalTime(event.date, event.endTime)
        ].filter(Boolean).join(' – ');
        const sportName = game?.sportType || game?.gameType || '';
        const gameStatus = game?.status || '';
        const isInProgress = gameStatus === 'in_progress' || gameStatus === 'active';
        const hasPlayers = game?.playerAName && game?.playerBName;
        const hasScore = hasPlayers && game?.playerAScore != null && game?.playerBScore != null;
        const gameName = typeof game?.gameName === 'string' ? game.gameName : '';
        const scoreText = scoreSummaryText(game?.scoreSummary);
        const status = STATUS_CONFIG[gameStatus] || (gameStatus ? { label: gameStatus, cls: 'bg-gray-100 text-gray-600' } : null);

        return (
          <div
            key={`${event.id}-${game?.id}`}
            onClick={() => onEventClick(event)}
            className={`p-4 cursor-pointer transition-colors ${theme.cardHover} group`}
          >
            {/* Row 1: live dot + event name + status */}
            <div className="flex items-center gap-2 mb-2">
              <span className="flex shrink-0 h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-2.5 w-2.5 rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
              </span>
              <p className={`font-semibold text-sm ${theme.text} truncate flex-1`}>{event.subject}</p>
              {status && (
                <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${status.cls}`}>{status.label}</span>
              )}
            </div>

            {/* Row 2: sport badge + time */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {sportName && (
                <span className="text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 px-2 py-0.5 rounded-full">{sportName}</span>
              )}
              {timeRange && <span className={`text-xs ${theme.textMuted}`}>{timeRange}</span>}
            </div>

            {/* Row 3: game info box — always shown when a game exists */}
            {game && (
              <div className={`rounded-lg px-3 py-2 mb-2 ${theme.backgroundSecondary} border ${theme.border}`}>
                {hasPlayers ? (
                  <div className="grid grid-cols-3 items-center gap-1">
                    <div className="text-center">
                      <p className={`text-xs font-medium ${theme.text} truncate`}>{game.playerAName}</p>
                      {hasScore && <p className="text-xl font-bold text-blue-500 leading-tight">{game.playerAScore}</p>}
                    </div>
                    <div className="text-center">
                      <span className={`text-xs font-bold ${theme.textMuted}`}>VS</span>
                      {scoreText && <p className="text-xs text-green-600 font-bold">{scoreText}</p>}
                    </div>
                    <div className="text-center">
                      <p className={`text-xs font-medium ${theme.text} truncate`}>{game.playerBName}</p>
                      {hasScore && <p className="text-xl font-bold text-orange-500 leading-tight">{game.playerBScore}</p>}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-xs font-semibold ${theme.text} truncate`}>{gameName || 'Game in progress'}</p>
                    {scoreText ? (
                      <span className="text-sm font-bold text-green-600 shrink-0">{scoreText}</span>
                    ) : isInProgress ? (
                      <span className="text-xs text-green-600 font-medium shrink-0">● Live</span>
                    ) : null}
                  </div>
                )}
              </div>
            )}

            {/* Row 4: participants + CTA */}
            <div className="flex items-center justify-between">
              <span className={`text-xs ${theme.textMuted}`}>
                {event.participantCount} participant{event.participantCount !== 1 ? 's' : ''}
              </span>
              <span className="text-xs font-semibold text-red-500 group-hover:text-red-600 transition-colors">
                Watch Live →
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LiveEventsList;
